import { createStyles, Flex, Group, Stack } from '@mantine/core';
import MarkdownRenderer from '@repo/common/components/MarkdownRenderer';
import { Badge, Icon, Text, Title } from '@repo/foundations';
import type { IApiListResponse, ISecodaEntity, ITag } from '../../../../api';
import { SecodaEntityIcon } from '../../../../components/SecodaEntity';
import { getEntityPathString } from '../../../../lib/utils/entity';
import type { AiOutputPreviewDataItem } from '../../types';

interface PreviewProps {
	resource: ISecodaEntity;
	data: AiOutputPreviewDataItem;
	tagsData?: IApiListResponse<ITag>;
}

const useStyles = createStyles((theme) => ({
	textWrapper: {
		padding: theme.spacing.md,
		borderRadius: theme.radius.sm,
		border: `1px solid ${theme.other.getColor('border/ai/default')}`,
		backgroundColor: theme.other.getColor('surface/ai/default'),
		// Override the color of the text inside the MarkdownRenderer
		'& *': {
			color: `${theme.other.getColor('text/ai/default')} !important`,
		},
	},
	badge: {
		height: 28,
		padding: `${theme.spacing['3xs']} ${theme.spacing.xs}`,
		border: `1px solid ${theme.other.getColor('border/ai/default')}`,
	},
}));

export function Preview({ data, resource, tagsData }: PreviewProps) {
	const { classes } = useStyles();

	const tagIds = Array.isArray(data?.tags) ? data.tags : [];
	const tags = tagsData?.results?.filter((tag) => tagIds.includes(tag.id));

	return (
		<Stack spacing="sm">
			<Group spacing="sm" noWrap>
				<SecodaEntityIcon entity={resource} size={32} />
				<Stack spacing="0">
					<Title size="sm">{resource?.title}</Title>
					<Text size="xs" color="text/secondary/default">
						{getEntityPathString(resource)}
					</Text>
				</Stack>
			</Group>
			{/* data fields are undefined if not apart of toggled on for output */}
			{(data?.pii !== undefined || data?.tags !== undefined) && (
				<Stack spacing="3xs">
					<Text size="sm" color="text/secondary/default">
						Properties
					</Text>
					<Group spacing="3xs">
						{data?.pii === true && (
							<Badge
								className={classes.badge}
								backgroundColor="surface/ai/default"
								color="text/primary/default"
							>
								<Group spacing="2xs" noWrap>
									<Icon name="shieldCheckFilled" />
									<Text size="sm" color="text/primary/default">
										PII
									</Text>
								</Group>
							</Badge>
						)}
						{data?.pii !== true && data?.pii !== undefined && (
							<Badge
								className={classes.badge}
								backgroundColor="surface/secondary/default"
								color="text/secondary/default"
							>
								<Group spacing="2xs" noWrap>
									<Icon name="shield" />
									<Text size="sm" color="text/secondary/default">
										No PII
									</Text>
								</Group>
							</Badge>
						)}
						{tags?.map((tag) => (
							<Badge
								key={tag.id}
								className={classes.badge}
								backgroundColor="surface/ai/default"
								color="text/primary/default"
							>
								<Group spacing="2xs" noWrap>
									<Icon name="circleFilled" />
									<Text size="sm" color="text/primary/default">
										{tag.name}
									</Text>
								</Group>
							</Badge>
						))}
						{data?.tags !== undefined && (!tags || tags.length === 0) && (
							<Badge
								className={classes.badge}
								backgroundColor="surface/secondary/default"
								color="text/secondary/default"
							>
								<Group spacing="2xs" noWrap>
									<Text size="sm" color="text/secondary/default">
										No tags
									</Text>
								</Group>
							</Badge>
						)}
					</Group>
				</Stack>
			)}
			{data?.description && (
				<Stack spacing="3xs">
					<Text size="sm" color="text/secondary/default">
						Description
					</Text>
					<Flex className={classes.textWrapper}>
						<Text size="sm" color="text/ai/default">
							{data.description}
						</Text>
					</Flex>
				</Stack>
			)}
			{data?.definition && (
				<Stack spacing="3xs">
					<Text size="sm" color="text/secondary/default">
						Definition
					</Text>
					<Flex className={classes.textWrapper}>
						<MarkdownRenderer>{data.definition}</MarkdownRenderer>
					</Flex>
				</Stack>
			)}
		</Stack>
	);
}
